import { Injectable, inject } from '@angular/core';
import { BleClient } from '@capacitor-community/bluetooth-le';
import { Capacitor } from '@capacitor/core';
import { createLogger } from '@hunting-nature/shared-core/logging';
import type { Observable } from 'rxjs';
import { Subject, defer, from, of, race, throwError, timer } from 'rxjs';
import { catchError, filter, map, retry, switchMap, take, tap, timeout } from 'rxjs/operators';

import type { BleDeviceInfo, BleServiceResponse, DeviceData } from '../../../interfaces/ble.interface';
import { DeviceType, CANNES_BLE_CONFIG } from '../../../interfaces/ble.interface';
import { BleRestProxyService } from '../../ble-rest-proxy.service';

import { BaseDeviceAdapter } from './base-device.adapter';

const SETUP_ABORTED_MESSAGE = 'Disconnected during setup';

interface ConnectState {
  setupComplete: boolean;
  abortSignal$: Subject<void> | null;
}

@Injectable({
  providedIn: 'root',
})
export class CannesDeviceAdapter extends BaseDeviceAdapter {
  readonly deviceType: DeviceType = DeviceType.CANNES;
  readonly deviceNamePrefix: string = CANNES_BLE_CONFIG.DEVICE_NAME_PREFIX;
  readonly identifyingService: string = CANNES_BLE_CONFIG.IDENTIFYING_SERVICE;

  private readonly bleRestProxyService = inject(BleRestProxyService);
  private readonly log = createLogger('CannesDeviceAdapter');
  private readonly CONNECT_TIMEOUT_MS = 10_000;
  private readonly BOND_TIMEOUT_MS = 30_000;
  private readonly BOND_POLL_INTERVAL_MS = 500;
  private readonly SETUP_SETTLE_MS = 500;
  private readonly PAIRING_VERIFY_TOTAL_MS = 30_000; // 30 s covers typical SMP pairing + encrypted reconnect; 60 s was unnecessarily long
  private readonly PAIRING_VERIFY_RETRY_DELAY_MS = 500;
  // Cannes drops the link after SMP pairing to re-establish it encrypted; first reconnects
  // can fail before bonded service discovery settles, hence progressive retry delays.
  private readonly POST_PAIRING_RECONNECT_MAX_RETRIES = 2;
  private readonly POST_PAIRING_RECONNECT_DELAYS_MS = [3_000, 5_000] as const;

  private readonly ENDPOINTS = {
    BLE_LOCAL_NAME: '/api/v1/services_ulm/ConnectivityService/BleLocalName/value',
    BUILD_VERSION: '/api/v1/services/BuildVersion/value',
    BATTERY_LEVELS: '/api/v1/services_ulm/BatteryService/BatteryLevels/value',
    BATTERY_TEMPERATURES: '/api/v1/services_ulm/BatteryService/BatteryTemperatures/value',
    SERIAL_NUMBER: '/api/v1/services_ulm/DeviceInformationService/SerialNumber/value',
    MODEL_NAME: '/api/v1/services_ulm/DeviceInformationService/ModelName/value',
    CONNECTIVITY_ENABLED_VALUE: '/api/v1/services_ulm/ConnectivityService/Enabled/value',
    WIFI_ENABLED: '/api/v1/services_ulm/ConnectivityService/WifiEnabled',
    WIFI_ENABLED_VALUE: '/api/v1/services_ulm/ConnectivityService/WifiEnabled/value',
    ACCESS_POINT_SSID_VALUE: '/api/v1/services_ulm/ConnectivityService/AccessPointSSID/value',
    ACCESS_POINT_PASSPHRASE_VALUE: '/api/v1/services_ulm/ConnectivityService/AccessPointWPA2Passphrase/value',
    WIFI_MODE_VALUE: '/api/v1/services_ulm/ConnectivityService/WifiMode/value',
    STREAM_START_VALUE: '/api/v1/services_ulm/RecordingService/StreamStart/value',
    STREAMING_STATUS_VALUE: '/api/v1/services_ulm/RecordingService/StreamingStatus/value',
    BRIGHTNESS_LEVEL: '/api/v1/services_ulm/ImagePipelineService/DisplayBrightnessLevel',
    BRIGHTNESS_LEVEL_VALUE: '/api/v1/services_ulm/ImagePipelineService/DisplayBrightnessLevel/value',
    CONTRAST_LEVEL: '/api/v1/services_ulm/ImagePipelineService/ContrastLevel',
    CONTRAST_LEVEL_VALUE: '/api/v1/services_ulm/ImagePipelineService/ContrastLevel/value',
    IMAGE_MODE: '/api/v1/services_ulm/ImagePipelineService/ImageMode',
    IMAGE_MODE_VALUE: '/api/v1/services_ulm/ImagePipelineService/ImageMode/value',
  } as const;

  connect$(deviceId: string, onDisconnect: (deviceId: string) => void): Observable<BleServiceResponse<BleDeviceInfo>> {
    const state: ConnectState = { setupComplete: false, abortSignal$: null };

    const onBleDisconnect = (disconnectedDeviceId: string): void => {
      if (!state.setupComplete) {
        state.abortSignal$?.next();
        return;
      }
      this.bleRestProxyService.cleanupDevice(disconnectedDeviceId);
      onDisconnect(disconnectedDeviceId);
    };

    return this.attemptSetup$(deviceId, onBleDisconnect, state).pipe(
      retry({
        count: this.POST_PAIRING_RECONNECT_MAX_RETRIES,
        delay: (error: unknown, retryIndex: number) => {
          const message = this.extractPluginErrorMessage(error);
          const isPostPairingReconnect = message.includes('Connection timeout') || message === SETUP_ABORTED_MESSAGE;
          if (!isPostPairingReconnect) return throwError(() => error);
          const delayIndex = Math.min(retryIndex - 1, this.POST_PAIRING_RECONNECT_DELAYS_MS.length - 1);
          return timer(this.POST_PAIRING_RECONNECT_DELAYS_MS[delayIndex]);
        },
      }),
      tap(() => {
        state.setupComplete = true;
      }),
      map(
        (): BleServiceResponse<BleDeviceInfo> => ({
          success: true,
          data: {
            deviceId,
            name: 'Cannes Device',
            isConnected: true,
          },
        })
      ),
      catchError((error: unknown) =>
        from(BleClient.disconnect(deviceId).catch(() => {})).pipe(
          tap(() => this.bleRestProxyService.resetDeviceNotificationState(deviceId)),
          switchMap(() => {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            return throwError(() => ({
              success: false,
              error: `Connection failed: ${errorMessage}`,
            }));
          })
        )
      )
    );
  }

  // defer() so each retry resubscribes runs a fresh BleClient.disconnect before reconnect;
  // without it CoreBluetooth stays half-torn-down and service discovery never completes.
  private attemptSetup$(deviceId: string, onBleDisconnect: (id: string) => void, state: ConnectState): Observable<void> {
    return defer(() => {
      state.setupComplete = false;
      state.abortSignal$ = new Subject<void>();
      const abort$ = state.abortSignal$.pipe(
        take(1),
        switchMap(() => throwError(() => new Error(SETUP_ABORTED_MESSAGE)))
      );

      const setup$ = from(BleClient.disconnect(deviceId).catch(() => {})).pipe(
        switchMap(() => from(BleClient.connect(deviceId, onBleDisconnect, { timeout: this.CONNECT_TIMEOUT_MS }))),
        switchMap(() => this.discoverAndValidateServices$(deviceId)),
        switchMap(() => this.bleRestProxyService.resolveServiceUuid$(deviceId)),
        switchMap(() => this.verifyPairing$(deviceId)),
        switchMap(() => this.androidEnsureBonded$(deviceId)),
        switchMap(() => this.bleRestProxyService.ensureNotificationsSetup$(deviceId)),
        switchMap(() => timer(this.SETUP_SETTLE_MS)),
        map(() => void 0)
      );

      return race(setup$, abort$);
    });
  }

  // Pairing gate: retry an encrypted write until pairing completes (write succeeds), is rejected
  // ("Encryption is insufficient.") or the device disconnects. Write timeouts mean pairing in progress.
  private verifyPairing$(deviceId: string): Observable<void> {
    const probe$ = defer(() =>
      from(
        BleClient.write(
          deviceId,
          this.bleRestProxyService.getServiceUuid(deviceId),
          CANNES_BLE_CONFIG.CHARACTERISTICS.URI_ENDPOINT,
          new DataView(new ArrayBuffer(0))
        )
      )
    );

    return probe$.pipe(
      retry({
        delay: (error: unknown) => {
          const message = this.extractPluginErrorMessage(error);
          if (message.includes('Encryption is insufficient') || message.includes('Not connected')) {
            return throwError(() => error);
          }
          return timer(this.PAIRING_VERIFY_RETRY_DELAY_MS);
        },
      }),
      timeout(this.PAIRING_VERIFY_TOTAL_MS),
      map(() => void 0)
    );
  }

  private extractPluginErrorMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    if (typeof error === 'object' && error !== null) {
      const object = error as Record<string, unknown>;
      return String(object['errorMessage'] ?? object['message'] ?? '');
    }
    return String(error);
  }

  // Races the device's Slave Security Request (via isBonded polling) against an explicit createBond fallback.
  private androidEnsureBonded$(deviceId: string): Observable<void> {
    if (Capacitor.getPlatform() !== 'android') return of(void 0);
    return from(BleClient.isBonded(deviceId)).pipe(
      switchMap(bonded => {
        if (bonded) return of(void 0);
        const createBond$ = from(BleClient.createBond(deviceId)).pipe(catchError(() => of(void 0)));
        return race(this.waitUntilBonded$(deviceId), createBond$.pipe(switchMap(() => this.waitUntilBonded$(deviceId))));
      })
    );
  }

  private waitUntilBonded$(deviceId: string): Observable<void> {
    const timeout$ = timer(this.BOND_TIMEOUT_MS).pipe(switchMap(() => throwError(() => new Error('Bonding did not complete in time'))));
    const poll$ = timer(0, this.BOND_POLL_INTERVAL_MS).pipe(
      switchMap(() => from(BleClient.isBonded(deviceId))),
      filter(bonded => bonded === true),
      take(1),
      map(() => void 0)
    );
    return race(poll$, timeout$);
  }

  disconnect$(deviceId: string): Observable<BleServiceResponse<void>> {
    this.bleRestProxyService.cleanupDevice(deviceId);

    return from(BleClient.disconnect(deviceId)).pipe(
      map(() => ({ success: true, data: undefined })),
      catchError((error: unknown) => {
        this.log.error('Disconnect failed', { deviceId, error });
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return of({
          success: false,
          error: `Disconnect failed: ${errorMessage}`,
        });
      })
    );
  }

  readBatteryLevel$(_deviceId: string): Observable<BleServiceResponse<number>> {
    return throwError(() => ({
      success: false,
      error: 'Battery level not supported on Cannes device',
    }));
  }

  readBuildVersion(deviceId: string): Observable<BleServiceResponse<string>> {
    return this.bleRestProxyService.get$<string>(deviceId, this.ENDPOINTS.BUILD_VERSION).pipe(
      map(value => ({ success: true, data: value })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to read build version: ${errorMessage}`,
        }));
      })
    );
  }

  readBatteryLevels(deviceId: string): Observable<BleServiceResponse<Array<number>>> {
    return this.bleRestProxyService.get$<Array<number>>(deviceId, this.ENDPOINTS.BATTERY_LEVELS).pipe(
      map(value => ({ success: true, data: value })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to read battery levels: ${errorMessage}`,
        }));
      })
    );
  }

  readBatteryTemperatures(deviceId: string): Observable<BleServiceResponse<Array<number>>> {
    return this.bleRestProxyService.get$<Array<number>>(deviceId, this.ENDPOINTS.BATTERY_TEMPERATURES).pipe(
      map(value => ({ success: true, data: value })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to read battery temperatures: ${errorMessage}`,
        }));
      })
    );
  }

  loadInitialData$(deviceId: string, updateData: (data: Partial<DeviceData>) => void): Observable<void> {
    return this.readBuildVersion(deviceId).pipe(
      tap(result => {
        if (result.success && result.data) updateData({ buildVersion: result.data });
      }),
      switchMap(() => this.readBatteryLevels(deviceId)),
      tap(result => {
        if (result.success && result.data) updateData({ batteryLevels: result.data });
      }),
      switchMap(() => this.readBatteryTemperatures(deviceId)),
      tap(result => {
        if (result.success && result.data) updateData({ batteryTemperatures: result.data });
      }),
      switchMap(() => this.readSerialNumber(deviceId)),
      tap(result => {
        if (result.success && result.data) updateData({ serialNumber: result.data });
      }),
      map(() => {})
    );
  }

  readSerialNumber(deviceId: string): Observable<BleServiceResponse<string>> {
    return this.bleRestProxyService.get$<string>(deviceId, this.ENDPOINTS.SERIAL_NUMBER).pipe(
      map(value => ({ success: true, data: value })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to read serial number: ${errorMessage}`,
        }));
      })
    );
  }

  readModelName(deviceId: string): Observable<BleServiceResponse<string>> {
    return this.bleRestProxyService.get$<string>(deviceId, this.ENDPOINTS.MODEL_NAME).pipe(
      map(value => ({ success: true, data: value })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to read model name: ${errorMessage}`,
        }));
      })
    );
  }

  readBleLocalName(deviceId: string): Observable<BleServiceResponse<string>> {
    return this.bleRestProxyService.get$<string>(deviceId, this.ENDPOINTS.BLE_LOCAL_NAME).pipe(
      map(value => ({ success: true, data: value })),
      catchError((error: unknown) => {
        this.log.error('Failed to read BLE local name', { deviceId, error });
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to read BLE local name: ${errorMessage}`,
        }));
      })
    );
  }

  writeBleLocalName(_deviceId: string, _name: string): Observable<BleServiceResponse<void>> {
    return throwError(() => ({
      success: false,
      error: 'Writing BLE local name is currently disabled',
    }));
  }

  readBrightnessLevel$(deviceId: string): Observable<BleServiceResponse<number>> {
    return this.bleRestProxyService.get$<number>(deviceId, this.ENDPOINTS.BRIGHTNESS_LEVEL).pipe(
      map(value => ({ success: true, data: value })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to read brightness level: ${errorMessage}`,
        }));
      })
    );
  }

  writeBrightnessLevel$(deviceId: string, level: number): Observable<BleServiceResponse<void>> {
    return this.bleRestProxyService.put$<void>(deviceId, this.ENDPOINTS.BRIGHTNESS_LEVEL_VALUE, level).pipe(
      map(() => ({ success: true, data: undefined })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to write brightness level: ${errorMessage}`,
        }));
      })
    );
  }

  readWifiEnabled$(deviceId: string): Observable<BleServiceResponse<boolean>> {
    return this.bleRestProxyService.get$<boolean>(deviceId, this.ENDPOINTS.WIFI_ENABLED_VALUE).pipe(
      map(value => ({ success: true, data: value })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to read WiFi enabled: ${errorMessage}`,
        }));
      })
    );
  }

  readContrastLevel$(deviceId: string): Observable<BleServiceResponse<number>> {
    return this.bleRestProxyService.get$<number>(deviceId, this.ENDPOINTS.CONTRAST_LEVEL_VALUE).pipe(
      map(value => ({ success: true, data: value })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to read contrast level: ${errorMessage}`,
        }));
      })
    );
  }

  writeContrastLevel$(deviceId: string, level: number): Observable<BleServiceResponse<void>> {
    return this.bleRestProxyService.put$<void>(deviceId, this.ENDPOINTS.CONTRAST_LEVEL, { value: level }).pipe(
      map(() => ({ success: true, data: undefined })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to write contrast level: ${errorMessage}`,
        }));
      })
    );
  }

  readImageMode$(deviceId: string): Observable<BleServiceResponse<number>> {
    return this.bleRestProxyService.get$<number>(deviceId, this.ENDPOINTS.IMAGE_MODE_VALUE).pipe(
      map(value => ({ success: true, data: value })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to read image mode: ${errorMessage}`,
        }));
      })
    );
  }

  writeImageMode$(deviceId: string, mode: number): Observable<BleServiceResponse<void>> {
    return this.bleRestProxyService.put$<void>(deviceId, this.ENDPOINTS.IMAGE_MODE, { value: mode }).pipe(
      map(() => ({ success: true, data: undefined })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to write image mode: ${errorMessage}`,
        }));
      })
    );
  }

  writeConnectivityEnabled$(deviceId: string, enabled: boolean): Observable<BleServiceResponse<void>> {
    return this.bleRestProxyService.put$<void>(deviceId, this.ENDPOINTS.CONNECTIVITY_ENABLED_VALUE, enabled).pipe(
      map(() => ({ success: true, data: undefined })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to write connectivity enabled: ${errorMessage}`,
        }));
      })
    );
  }

  writeWifiEnabled$(deviceId: string, enabled: boolean): Observable<BleServiceResponse<void>> {
    return this.bleRestProxyService.put$<void>(deviceId, this.ENDPOINTS.WIFI_ENABLED_VALUE, enabled).pipe(
      map(() => ({ success: true, data: undefined })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to write WiFi enabled: ${errorMessage}`,
        }));
      })
    );
  }

  writeStreamStart$(deviceId: string): Observable<BleServiceResponse<void>> {
    // StreamStart is a self-resetting trigger: writing true starts the on-device streaming
    // server; the device resets the value to false once the server is up.
    return this.bleRestProxyService.put$<void>(deviceId, this.ENDPOINTS.STREAM_START_VALUE, true).pipe(
      map(() => ({ success: true, data: undefined })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to write stream start: ${errorMessage}`,
        }));
      })
    );
  }

  readStreamingStatus$(deviceId: string): Observable<BleServiceResponse<boolean>> {
    return this.bleRestProxyService.get$<boolean>(deviceId, this.ENDPOINTS.STREAMING_STATUS_VALUE).pipe(
      map(value => ({ success: true, data: value })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to read streaming status: ${errorMessage}`,
        }));
      })
    );
  }

  readAccessPointSSID$(deviceId: string): Observable<BleServiceResponse<string>> {
    return this.bleRestProxyService.get$<string>(deviceId, this.ENDPOINTS.ACCESS_POINT_SSID_VALUE).pipe(
      map(value => ({ success: true, data: value })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to read access point SSID: ${errorMessage}`,
        }));
      })
    );
  }

  writeAccessPointPassphrase$(deviceId: string, passphrase: string): Observable<BleServiceResponse<void>> {
    // Device expects JSON-encoded value (e.g. `"abc"`); the proxy's serializeBody() skips
    // JSON.stringify for raw string bodies, so we must quote it here to keep the body valid JSON.
    return this.bleRestProxyService.put$<void>(deviceId, this.ENDPOINTS.ACCESS_POINT_PASSPHRASE_VALUE, JSON.stringify(passphrase)).pipe(
      map(() => ({ success: true, data: undefined })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to write access point passphrase: ${errorMessage}`,
        }));
      })
    );
  }

  writeWifiMode$(deviceId: string, mode: number): Observable<BleServiceResponse<void>> {
    return this.bleRestProxyService.put$<void>(deviceId, this.ENDPOINTS.WIFI_MODE_VALUE, mode).pipe(
      map(() => ({ success: true, data: undefined })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to write WiFi mode: ${errorMessage}`,
        }));
      })
    );
  }

  loadSettingsData$(deviceId: string): Observable<{ brightnessLevel: number; wifiEnabled: boolean; contrastLevel: number; imageMode: number }> {
    let brightnessLevel: number;
    let wifiEnabled: boolean;
    let contrastLevel: number;

    return this.bleRestProxyService.get$<{ value: number }>(deviceId, this.ENDPOINTS.BRIGHTNESS_LEVEL).pipe(
      tap(res => (brightnessLevel = res.value)),
      switchMap(() => this.bleRestProxyService.get$<{ value: boolean }>(deviceId, this.ENDPOINTS.WIFI_ENABLED)),
      tap(res => (wifiEnabled = res.value)),
      switchMap(() => this.bleRestProxyService.get$<number>(deviceId, this.ENDPOINTS.CONTRAST_LEVEL_VALUE)),
      tap(res => (contrastLevel = res)),
      switchMap(() => this.bleRestProxyService.get$<number>(deviceId, this.ENDPOINTS.IMAGE_MODE_VALUE)),
      map(imageMode => ({ brightnessLevel, wifiEnabled, contrastLevel, imageMode }))
    );
  }

  readDeviceInfo$(deviceId: string): Observable<BleServiceResponse<Partial<DeviceData>>> {
    return this.readBleLocalName(deviceId).pipe(
      map(response => ({
        success: true,
        data: {
          name: response.data,
        } as Partial<DeviceData>,
      })),
      catchError((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        return throwError(() => ({
          success: false,
          error: `Failed to read device info: ${errorMessage}`,
        }));
      })
    );
  }

  override canHandleDevice(deviceName: string, advertisedServices?: Array<string>): boolean {
    if (advertisedServices?.some(uuid => uuid.toLowerCase() === this.identifyingService)) {
      return true;
    }
    return deviceName.startsWith(this.deviceNamePrefix) || deviceName.startsWith(CANNES_BLE_CONFIG.DEVICE_NAME_PREFIX_ALTERNATIVE);
  }
}
